<template>
    <div class="outlet-item-picker">
        <div class="outlet-item-picker-header">
            <div class="outlet-item-picker-input">
                <select 
                    v-model="selectedItemId" 
                    class="outlet-item-picker-select"
                    :disabled="disabled"
                    @change="onItemSelected"
                >
                    <option :value="null">-- Select Outlet Item --</option>
                    <option 
                        v-for="option in hierarchicalOptions" 
                        :key="option.id" 
                        :value="option.id"
                    >
                        {{ option.label }}
                    </option>
                </select>
            </div>
            <div class="outlet-item-picker-pagination">
                <a @click="!disabled && refresh()" :class="{ 'outlet-item-picker-link-disabled': disabled }">REFRESH</a>
            </div>
        </div>
        <div v-show="isLoading" class="outlet-item-picker-loading">
            <span class="icon"><img :src="`/Assets/img/spin.svg`" /></span> LOADING
        </div>
        <div v-show="isSelectingItem" class="outlet-item-picker-loading">
            <span class="icon"><img :src="`/Assets/img/spin.svg`" /></span> Loading outlet item...
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from 'vue'
import api from '@/api'
import { OutletItem } from '@/classes/OutletItem'

interface HierarchicalOption {
    id: string;
    label: string;
    item: OutletItem;
}

export default defineComponent({
    name: 'OutletItemPicker',
    props: {
        modelValue: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'outletItemSelected'],
    setup(props, { emit }) {
        const outletItems = ref<OutletItem[]>([])
        const isLoading = ref(false)
        const isSelectingItem = ref(false)
        const selectedItemId = ref<string | null>(props.modelValue)

        watch(() => props.modelValue, (newValue) => {
            selectedItemId.value = newValue
        })

        const buildHierarchicalOptions = (items: OutletItem[]): HierarchicalOption[] => {
            const options: HierarchicalOption[] = []
            const itemMap = new Map<string, OutletItem>()
            
            // First pass: create map of all items
            items.forEach(item => {
                itemMap.set(item.id, item)
            })

            // Second pass: build hierarchical labels
            const buildLabel = (item: OutletItem, depth: number = 0): string => {
                if (!item.parentId || !itemMap.has(item.parentId)) {
                    return item.name
                }
                
                const parent = itemMap.get(item.parentId)!
                const parentLabel = buildLabel(parent, depth + 1)
                return `${parentLabel} > ${item.name}`
            }

            items.forEach(item => {
                if (item.isActive) {
                    options.push({
                        id: item.id,
                        label: buildLabel(item),
                        item: item
                    })
                }
            })

            // Sort by label for better UX
            options.sort((a, b) => a.label.localeCompare(b.label))
            
            return options
        }

        const hierarchicalOptions = computed(() => {
            return buildHierarchicalOptions(outletItems.value)
        })

        const getOutletItems = async () => {
            isLoading.value = true
            try {
                const response = await api.getOutletItems()
                outletItems.value = response.data || []
            } catch (error) {
                console.error('Failed to load outlet items:', error)
                outletItems.value = []
            } finally {
                isLoading.value = false
            }
        }

        const refresh = () => {
            if (props.disabled) {
                return
            }
            getOutletItems()
        }

        const onItemSelected = () => {
            if (props.disabled || !selectedItemId.value) {
                return
            }
            
            isSelectingItem.value = true
            emit('update:modelValue', selectedItemId.value)
            
            const option = hierarchicalOptions.value.find(opt => opt.id === selectedItemId.value)
            if (option) {
                emit('outletItemSelected', option.item)
            }
            
            isSelectingItem.value = false
        }

        onMounted(() => {
            getOutletItems()
            if (props.modelValue) {
                onItemSelected()
            }
        })

        return {
            outletItems,
            hierarchicalOptions,
            isLoading,
            isSelectingItem,
            selectedItemId,
            refresh,
            onItemSelected
        }
    }
})
</script>

<style scoped>
.outlet-item-picker {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.outlet-item-picker-header {
    display: flex;
    align-items: center;
    gap: 15px;
    width: 100%;
}

.outlet-item-picker-input {
    flex: 1;
}

.outlet-item-picker-select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    box-sizing: border-box;
    background: white;
    cursor: pointer;
}

.outlet-item-picker-select:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.outlet-item-picker-select:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.outlet-item-picker-pagination {
    display: flex;
    gap: 15px;
    align-items: center;
    flex-shrink: 0;
}

.outlet-item-picker-pagination a {
    cursor: pointer;
    color: #2196F3;
    text-decoration: none;
    font-weight: 500;
    padding: 5px 10px;
    border-radius: 4px;
    transition: background-color 0.2s ease;
}

.outlet-item-picker-pagination a:hover:not(.outlet-item-picker-link-disabled) {
    background-color: #e3f2fd;
}

.outlet-item-picker-link-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
}

.outlet-item-picker-loading {
    text-align: center;
    padding: 20px;
    color: #666;
}

.outlet-item-picker-loading .icon {
    display: inline-block;
    margin-right: 10px;
}

.outlet-item-picker-loading .icon img {
    width: 20px;
    height: 20px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>

